import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faInfoCircle, faWarning } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import type { HexColor } from '@sorare/core/src/style/types';

import { TargetIcon } from '#src/assets/icons/TargetIcon.tsx';
import { LineupRewardsDistances } from '#src/components/lineup/LineupRewardsDistances/index.tsx';
import {
  ContenderLeftFooterState,
  getContenderLeftFooterState,
} from '#src/lib/contender.ts';

import { SimpleContenderFooterStatus } from './SimpleContenderFooterStatus/index.tsx';
import type { LeftLiveLineupFooter_so5LeaderboardContender } from './__generated__/index.graphql.ts';

type Props = {
  so5LeaderboardContender: LeftLiveLineupFooter_so5LeaderboardContender;
};

export const LeftLiveLineupFooter = ({ so5LeaderboardContender }: Props) => {
  const { so5Lineup, slug } = so5LeaderboardContender;
  const so5Ranking = so5Lineup?.so5Rankings.at(0);
  const leftState = getContenderLeftFooterState(so5LeaderboardContender);
  if (leftState === null || !so5Ranking) {
    return null;
  }

  const { state } = leftState;

  const lineupDistances = so5Ranking && (
    <Dropdown
      horizontalGap={-8}
      label={props => (
        <IconButton
          {...props}
          icon={faInfoCircle}
          color="transparent"
          size="small"
        />
      )}
    >
      {({ closeDropdown }) => (
        <LineupRewardsDistances
          so5LeaderboardContenderSlug={slug}
          onClose={closeDropdown}
        />
      )}
    </Dropdown>
  );

  let icon: ReactNode = null;

  switch (state) {
    case ContenderLeftFooterState.CANCELLED:
      icon = <FontAwesomeIcon icon={faWarning} color="var(--c-yellow-400)" />;
      break;
    case ContenderLeftFooterState.NOT_STARTED:
      icon = lineupDistances;
      break;
    case ContenderLeftFooterState.REWARDS:
      icon = lineupDistances;
      break;
    case ContenderLeftFooterState.POINTS_TO_CLOSEST_REWARD:
      icon = lineupDistances;
      break;
    case ContenderLeftFooterState.POINTS_TO_SAFETY:
      icon = lineupDistances;
      break;
    case ContenderLeftFooterState.POINTS_TO_TARGET:
    case ContenderLeftFooterState.POINTS_TO_THRESHOLD:
      if (state === ContenderLeftFooterState.POINTS_TO_TARGET) {
        icon = <img src={leftState.iconUrl} alt="" height={12} />;
      } else if (state === ContenderLeftFooterState.POINTS_TO_THRESHOLD) {
        icon = (
          <TargetIcon
            color={`rgb(${leftState.color})` as HexColor}
            size={1.5}
          />
        );
      }
      break;
    default:
      return null;
  }

  return (
    <Horizontal gap={0.5}>
      {icon}
      <LabelM color="var(--c-nd-600)">
        <SimpleContenderFooterStatus
          so5LeaderboardContender={so5LeaderboardContender}
        />
      </LabelM>
    </Horizontal>
  );
};

LeftLiveLineupFooter.fragments = {
  so5LeaderboardContender: gql`
    fragment LeftLiveLineupFooter_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        so5Rankings {
          id
        }
      }
      ...getContenderLeftFooterState_so5LeaderboardContender
      ...SimpleContenderFooterStatus_so5LeaderboardContender
    }
    ${getContenderLeftFooterState.fragments.so5LeaderboardContender}
    ${SimpleContenderFooterStatus.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<LeftLiveLineupFooter_so5LeaderboardContender>,
};
